import React, { createContext, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { ChevronsLeft, ChevronsRight, Eye, EyeOff } from 'lucide-react';
import { BrandLoader } from './BrandLogo';
import { PASSWORD_RULES } from '@physioedge/shared';

/**
 * Full-screen overlay portaled to document.body so it paints above the sticky
 * header (z-40) and mobile bottom nav (z-40), not underneath them.
 */
export function ModalOverlay({
  children,
  onBackdropClick,
  className = '',
}: {
  children: React.ReactNode;
  onBackdropClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  className?: string;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  useEffect(() => {
    if (!mounted) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mounted]);
  if (!mounted) return null;

  return createPortal(
    <div
      className={`app-modal-root fixed inset-0 flex items-end sm:items-center justify-center overflow-y-auto overscroll-contain bg-black/55 backdrop-blur-sm p-0 sm:px-4 sm:py-8 ${className}`}
      onClick={onBackdropClick}
      role="presentation"
    >
      {children}
    </div>,
    document.body,
  );
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  children,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const base = 'inline-flex items-center justify-center gap-2 font-medium rounded-radius-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sand/50 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation';

  const variants = {
    primary: 'bg-sand text-white hover:opacity-90 active:opacity-80',
    secondary: 'bg-teal text-white hover:opacity-90',
    ghost: 'bg-transparent text-ink hover:bg-surface border border-line',
    danger: 'bg-danger text-white hover:opacity-90',
  };

  const sizes = {
    sm: 'text-xs px-3 py-2 min-h-[36px]',
    md: 'text-sm px-4 py-2.5 min-h-[44px]',
    lg: 'text-base px-6 py-3.5 min-h-[52px]',
  };

  return (
    <button
      className={`${base} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <>
          <span
            className="inline-block w-4 h-4 rounded-full border-2 border-current border-r-transparent animate-spin shrink-0"
            aria-hidden
          />
          <span>{typeof children === 'string' ? children : 'Please wait…'}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}

export function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-surface rounded-radius border border-line shadow-card ${className}`}>
      {children}
    </div>
  );
}

export function Badge({
  children,
  variant = 'default',
}: {
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info';
}) {
  const variants = {
    default: 'bg-teal-soft text-teal',
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    danger: 'bg-danger-soft text-danger',
    info: 'bg-sand-soft text-sand-strong',
  };
  return (
    <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${variants[variant]}`}>
      {children}
    </span>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; variant: 'success' | 'warning' | 'danger' | 'default' | 'info' }> = {
    active: { label: 'Active', variant: 'success' },
    upcoming: { label: 'Upcoming', variant: 'info' },
    finished: { label: 'Finished', variant: 'default' },
    inactive: { label: 'Inactive', variant: 'default' },
    published: { label: 'Published', variant: 'success' },
    unpublished: { label: 'Unpublished', variant: 'default' },
    draft: { label: 'Draft', variant: 'default' },
    approved: { label: 'Approved', variant: 'success' },
    answered: { label: 'Answered', variant: 'success' },
    completed: { label: 'Completed', variant: 'success' },
    pending: { label: 'Pending', variant: 'warning' },
    pending_review: { label: 'Under review', variant: 'warning' },
    frozen: { label: 'Frozen', variant: 'info' },
    booked: { label: 'Booked', variant: 'info' },
    scheduled: { label: 'Scheduled', variant: 'info' },
    requested: { label: 'Requested', variant: 'warning' },
    expired: { label: 'Expired', variant: 'default' },
    cancelled: { label: 'Cancelled', variant: 'default' },
    rejected: { label: 'Rejected', variant: 'danger' },
    declined: { label: 'Declined', variant: 'danger' },
    under_review: { label: 'Under review', variant: 'warning' },
    paid: { label: 'Paid', variant: 'success' },
    available: { label: 'Ready', variant: 'info' },
    failed: { label: 'Failed', variant: 'danger' },
    sent: { label: 'Sent', variant: 'success' },
  };
  const { label, variant } = map[status] ?? { label: status, variant: 'default' as const };
  return <Badge variant={variant}>{label}</Badge>;
}

export function Alert({
  children,
  type = 'error',
  className = '',
}: {
  children: React.ReactNode;
  type?: 'error' | 'success' | 'warning' | 'info';
  className?: string;
}) {
  const styles = {
    error: 'bg-danger-soft text-danger border-danger/30',
    success: 'bg-success-soft text-success border-success/30',
    warning: 'bg-warning-soft text-warning border-warning/30',
    info: 'bg-teal-soft text-teal border-teal/30',
  };
  return (
    <div className={`text-sm rounded-radius-sm border px-4 py-3 ${styles[type]} ${className}`}>
      {children}
    </div>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement> & { label?: string; error?: string }) {
  const { label, error, className = '', ...rest } = props;
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-xs font-medium text-ink-soft">{label}</label>}
      <input
        className={`border rounded-radius-sm px-3 py-3 text-sm bg-surface text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-sand/40 focus:border-sand transition-colors min-h-[48px] ${error ? 'border-danger' : 'border-line'} ${className}`}
        {...rest}
      />
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}

export function PasswordInput({
  label,
  error,
  className = '',
  ...rest
}: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & { label?: string; error?: string }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-xs font-medium text-ink-soft">{label}</label>}
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          className={`w-full border rounded-radius-sm pl-3 pr-11 py-3 text-sm bg-surface text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-sand/40 focus:border-sand transition-colors min-h-[48px] ${error ? 'border-danger' : 'border-line'} ${className}`}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setVisible(v => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-ink-faint hover:text-ink rounded-radius-sm"
          aria-label={visible ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}

export function PasswordChecklist({
  password,
  currentPassword,
}: {
  password: string;
  currentPassword?: string;
}) {
  if (!password) return null;
  const differentFromCurrent = !currentPassword || password !== currentPassword;
  return (
    <ul className="col-span-2 space-y-1 text-xs">
      {PASSWORD_RULES.map(rule => {
        const ok = rule.test(password);
        return (
          <li key={rule.id} className={`flex items-center gap-2 ${ok ? 'text-success' : 'text-ink-faint'}`}>
            <span className="w-3.5 text-center font-medium" aria-hidden>{ok ? '✓' : '○'}</span>
            <span>{rule.label}</span>
          </li>
        );
      })}
      {currentPassword ? (
        <li className={`flex items-center gap-2 ${differentFromCurrent ? 'text-success' : 'text-ink-faint'}`}>
          <span className="w-3.5 text-center font-medium" aria-hidden>{differentFromCurrent ? '✓' : '○'}</span>
          <span>Different from current password</span>
        </li>
      ) : null}
    </ul>
  );
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string; error?: string }) {
  const { label, error, className = '', ...rest } = props;
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-xs font-medium text-ink-soft">{label}</label>}
      <textarea
        className={`border rounded-radius-sm px-3 py-2 text-sm bg-surface text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-sand/40 focus:border-sand resize-none transition-colors ${error ? 'border-danger' : 'border-line'} ${className}`}
        rows={4}
        {...rest}
      />
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement> & { label?: string; error?: string; options: { value: string; label: string }[] }) {
  const { label, error, options, className = '', ...rest } = props;
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-xs font-medium text-ink-soft">{label}</label>}
      <select
        className={`border rounded-radius-sm px-3 py-2 text-sm bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-sand/40 focus:border-sand transition-colors ${error ? 'border-danger' : 'border-line'} ${className}`}
        {...rest}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div
      className={`animate-pulse rounded-lg bg-line ${className}`}
      aria-hidden
    />
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-line overflow-hidden bg-surface">
      <Skeleton className="aspect-[16/9] w-full rounded-none" />
      <div className="p-4 space-y-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  );
}

export function CardGridSkeleton({ count = 6, className }: { count?: number; className?: string }) {
  return (
    <div className={className ?? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6'}>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

export function TableSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <div className="rounded-2xl border border-line overflow-hidden bg-surface">
      <div className="px-4 py-3 border-b border-line flex gap-4">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-32 hidden sm:block" />
        <Skeleton className="h-3 w-20 ml-auto" />
      </div>
      <div className="divide-y divide-line">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="px-4 py-3.5 flex items-center gap-4">
            <Skeleton className="h-9 w-9 rounded-full shrink-0" />
            <div className="flex-1 space-y-2 min-w-0">
              <Skeleton className="h-3.5 w-2/5" />
              <Skeleton className="h-3 w-1/4" />
            </div>
            <Skeleton className="h-3 w-16 hidden sm:block" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function DetailSkeleton() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12 space-y-5">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="aspect-[16/9] w-full rounded-2xl" />
      <Skeleton className="h-9 w-3/4" />
      <Skeleton className="h-4 w-40" />
      <div className="space-y-2 pt-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    </div>
  );
}

export function FormSkeleton() {
  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="bg-surface border border-line rounded-2xl p-6 space-y-4">
        <Skeleton className="h-7 w-40 mx-auto" />
        <Skeleton className="h-4 w-56 mx-auto" />
        <Skeleton className="h-11 w-full mt-4" />
        <Skeleton className="h-11 w-full" />
        <Skeleton className="h-11 w-full" />
        <Skeleton className="h-12 w-full mt-2" />
      </div>
    </div>
  );
}

export function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-lg shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-3/5" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function CalendarSkeleton() {
  return (
    <div className="space-y-5">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-14 w-full rounded-xl" />
          <Skeleton className="h-14 w-full rounded-xl" />
        </div>
      ))}
    </div>
  );
}

export function PageLoader({
  variant = 'cards',
}: {
  variant?: 'cards' | 'table' | 'detail' | 'form' | 'lines' | 'calendar' | 'inline';
}) {
  const size = variant === 'inline' ? 'xs' : 'sm';
  const pad = variant === 'inline'
    ? 'py-5'
    : variant === 'form' || variant === 'cards' || variant === 'detail'
      ? 'py-12 sm:py-16'
      : 'py-8 sm:py-10';
  return (
    <div
      className={`flex flex-col items-center justify-center ${pad}`}
      role="status"
      aria-label="Loading"
    >
      <BrandLoader size={size} />
      <span className="sr-only">Loading</span>
    </div>
  );
}

export function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-32 text-ink-faint">
      <p className="text-sm">{message}</p>
    </div>
  );
}

const SIDEBAR_COLLAPSED_KEY = 'physioedge.sidebarCollapsed';

const SidebarCollapsedContext = createContext(false);

export function useSidebarCollapsed(): boolean {
  return useContext(SidebarCollapsedContext);
}

function readSidebarCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function SidebarNav({ items }: { items: { href: string; label: string; icon?: React.ReactNode }[] }) {
  const current = window.location.pathname;
  const collapsed = useSidebarCollapsed();
  return (
    <nav className="flex flex-col gap-1" aria-label="Section">
      {items.map(item => {
        const active = current === item.href
          || (item.href !== '/admin' && item.href !== '/student' && item.href !== '/doctor' && item.href !== '/clinic' && current.startsWith(item.href));
        return (
          <a
            key={item.href}
            href={item.href}
            title={item.label}
            aria-label={item.label}
            aria-current={active ? 'page' : undefined}
            className={`flex items-center gap-2.5 py-2 text-sm rounded-radius-sm transition-colors ${
              collapsed ? 'justify-center px-2' : 'px-3'
            } ${
              active
                ? 'bg-teal-soft text-teal font-medium'
                : 'text-ink-soft hover:text-ink hover:bg-surface'
            }`}
          >
            {item.icon
              ? <span className="shrink-0">{item.icon}</span>
              : collapsed
                ? <span className="text-xs font-semibold">{item.label.slice(0, 1)}</span>
                : null}
            {!collapsed && <span className="truncate">{item.label}</span>}
          </a>
        );
      })}
    </nav>
  );
}

export function DashboardShell({
  sidebar,
  children,
  mobileNavItems,
}: {
  sidebar?: React.ReactNode;
  children: React.ReactNode;
  /** When provided, renders a horizontal scrollable tab bar on mobile and controls active state via ?tab= search param. */
  mobileNavItems?: { key: string; label: string; icon?: React.ReactNode }[];
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? mobileNavItems?.[0]?.key ?? '';
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch { /* private mode */ }
      return next;
    });
  };

  const handleTabClick = (key: string) => {
    setSearchParams({ tab: key });
  };

  return (
    <div className="flex min-h-screen bg-bg">
      {sidebar && (
        <aside
          className={`hidden md:flex flex-col shrink-0 border-r border-line bg-surface py-4 ${
            collapsed ? 'w-[4.25rem] px-2' : 'w-56 px-3'
          }`}
        >
          <button
            type="button"
            onClick={toggleCollapsed}
            className="mb-3 flex items-center justify-center gap-2 min-h-[40px] rounded-radius-sm text-ink-soft hover:text-ink hover:bg-bg touch-manipulation"
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : (
              <>
                <ChevronsLeft className="w-4 h-4 shrink-0" />
                <span className="text-xs font-medium">Collapse</span>
              </>
            )}
          </button>
          <SidebarCollapsedContext.Provider value={collapsed}>
            <div className="flex-1 min-h-0">
              {sidebar}
            </div>
          </SidebarCollapsedContext.Provider>
        </aside>
      )}

      {/* Content column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile horizontal tab bar */}
        {mobileNavItems && (
          <div className="md:hidden bg-surface border-b border-line overflow-x-auto scrollbar-none">
            <div className="flex min-w-max px-2 py-1.5 gap-1">
              {mobileNavItems.map(item => {
                const active = item.key === activeTab;
                return (
                  <button
                    key={item.key}
                    onClick={() => handleTabClick(item.key)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium whitespace-nowrap rounded-radius-sm transition-colors touch-manipulation min-h-[40px] ${
                      active
                        ? 'bg-teal-soft text-teal'
                        : 'text-ink-soft hover:text-ink hover:bg-bg'
                    }`}
                  >
                    {item.icon && <span className="shrink-0">{item.icon}</span>}
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <main className="flex-1 px-4 md:px-8 py-5 md:py-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
  leading,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  leading?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between mb-6 gap-3">
      <div className="flex items-center gap-3 min-w-0">
        {leading}
        <div className="min-w-0">
          <h1 className="text-2xl font-heading font-bold text-ink truncate">{title}</h1>
          {subtitle && <p className="text-sm text-ink-soft mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function Table<T>({
  columns,
  data,
  empty,
  onRowClick,
  getRowKey,
}: {
  columns: { key: string; header: string; render: (row: T) => React.ReactNode }[];
  data: T[];
  empty?: string;
  onRowClick?: (row: T) => void;
  getRowKey?: (row: T, index: number) => string | number;
}) {
  if (data.length === 0) return <EmptyState message={empty ?? 'No records found.'} />;
  return (
    <div className="overflow-x-auto rounded-radius border border-line">
      <table className="w-full text-sm">
        <thead className="bg-bg border-b border-line">
          <tr>
            {columns.map(col => (
              <th key={col.key} className="text-left px-4 py-3 font-medium text-ink-soft text-xs uppercase tracking-wide">
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {data.map((row, i) => (
            <tr
              key={getRowKey ? getRowKey(row, i) : i}
              className={`hover:bg-bg transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={onRowClick ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onRowClick(row);
                }
              } : undefined}
            >
              {columns.map(col => (
                <td key={col.key} className="px-4 py-3 text-ink">
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
